import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/utils/api';
import { StaffMessageBody } from '@/components/staff-messaging/StaffMessageBody';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

type AdminConv = {
  id: string;
  type: string;
  title: string | null;
  updatedAt: string;
  /** Opzionale se il backend è vecchio; calcoliamo sempre dai members lato UI. */
  auditLabel?: string;
  members: { userId: string; user: { firstName: string | null; lastName: string | null; email: string } }[];
  _count: { messages: number };
};

type AdminMsg = {
  id: string;
  conversationId: string;
  senderId: string;
  senderLabel?: string;
  body: string;
  createdAt: string;
  deletedAt: string | null;
  deletedById: string | null;
  deleted: boolean;
};

function userDisplayName(u: { firstName: string | null; lastName: string | null; email: string }) {
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return n || u.email || 'Utente';
}

/** Sempre da dati conversazione: DM = "Nome ↔ Nome", gruppo = "Gruppo: …". */
function conversationAuditLabel(c: AdminConv): string {
  const fromApi = c.auditLabel?.trim();
  if (fromApi) return fromApi;

  const isGroup = c.type === 'GROUP' || c.type === 'group';
  if (isGroup) {
    const t = c.title?.trim();
    return t ? `Gruppo: ${t}` : `Gruppo (${c.members?.length ?? 0} membri)`;
  }

  const peers = (c.members ?? [])
    .map((m) => userDisplayName(m.user))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'it'));
  if (peers.length >= 2) return `${peers[0]} ↔ ${peers[1]}`;
  if (peers.length === 1) return `DM: ${peers[0]}`;
  return `Conversazione ${c.id.slice(0, 10)}…`;
}

function messageSenderDisplay(m: AdminMsg): string {
  const s = m.senderLabel?.trim();
  if (s) return s;
  if (m.senderId) return `Utente (id ${m.senderId.slice(0, 12)}…)`;
  return 'Sconosciuto';
}

export default function StaffMessagingAdminPage() {
  const qc = useQueryClient();
  const [convId, setConvId] = useState<string>('');

  const { data: convs = [], isLoading: loadingConvs } = useQuery({
    queryKey: ['staff-messaging', 'admin', 'conversations'],
    queryFn: async () => {
      const r = await apiClient.get('/admin/staff-messaging/conversations');
      return r.data.data as AdminConv[];
    },
  });

  const { data: msgPack, isLoading: loadingMsgs } = useQuery({
    queryKey: ['staff-messaging', 'admin', 'messages', convId],
    queryFn: async () => {
      const r = await apiClient.get(`/admin/staff-messaging/conversations/${convId}/messages`, {
        params: { limit: 100 },
      });
      return r.data.data as { messages: AdminMsg[]; nextCursor: string | null };
    },
    enabled: !!convId,
  });

  const selectedConvSummary = useMemo(() => {
    const c = convs.find((x) => x.id === convId);
    if (!c) return '';
    return `${conversationAuditLabel(c)} · ${c._count.messages} messaggi`;
  }, [convs, convId]);

  const deleteAsAdmin = async (messageId: string) => {
    if (!window.confirm('Eliminare definitivamente (moderazione) questo messaggio?')) return;
    try {
      await apiClient.delete(`/admin/staff-messaging/messages/${messageId}`);
      toast.success('Messaggio moderato');
      void qc.invalidateQueries({ queryKey: ['staff-messaging', 'admin'] });
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Errore');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Messaggi interni (audit)</h1>
        <p className="text-sm text-gray-600">
          Lettura globale delle conversazioni e dei messaggi, inclusi contenuti eliminati (solo amministratori).
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <label className="mb-2 block text-sm font-medium">Conversazione</label>
        {loadingConvs ? (
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        ) : (
          <Select value={convId} onValueChange={setConvId}>
            <SelectTrigger className="max-w-2xl text-left">
              <SelectValue placeholder="Seleziona una conversazione (partecipanti visibili nel menu)">
                {convId ? selectedConvSummary || '…' : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {convs.map((c) => {
                const line = `${conversationAuditLabel(c)} · ${c._count.messages} messaggi`;
                return (
                  <SelectItem key={c.id} value={c.id}>
                    {line}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </div>

      {convId && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold">Messaggi</h2>
          {loadingMsgs ? (
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          ) : (
            <ul className="space-y-3">
              {(msgPack?.messages ?? []).map((m) => (
                <li key={m.id} className="rounded-md border border-gray-100 p-3 text-sm">
                  <p className="text-sm font-medium text-gray-900">
                    Mittente:{' '}
                    <span className="text-blue-800">{messageSenderDisplay(m)}</span>
                  </p>
                  <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                    <span>{new Date(m.createdAt).toLocaleString('it-IT')}</span>
                    <span className="font-mono text-[10px]">{m.id}</span>
                  </div>
                  {m.deleted && (
                    <p className="mt-1 text-xs font-medium text-amber-700">
                      Eliminato — audit conserva il testo originale.
                    </p>
                  )}
                  <div className="mt-2 text-gray-900">
                    <StaffMessageBody text={m.body} />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="mt-2"
                    onClick={() => void deleteAsAdmin(m.id)}
                  >
                    Moderazione: elimina
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
